import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { isAuthenticated } from "../../services/authService";
import {
  documentRemotePathFromDocumentUrl,
  getHostDocumentUrlAsync
} from "../../services/hostDocumentInfoService";
import {
  resolveGeneratedContractByDocumentUrl,
  type ResolveByDocumentUrlResult
} from "../../services/generatedContractResolveService";
import { decideResolveNavigation } from "../../services/resolveNavigationService";

type BannerTone = "info" | "error";

type GateBanner = {
  readonly tone: BannerTone;
  readonly message: string;
};

type HostDocReadyState = { readonly hostDocumentOpenedFromList?: boolean };

const isReviewLikeRoute = (path: string): boolean =>
  path.startsWith("/contractiq/contracts/") ||
  path.startsWith("/contractiq/templates/") ||
  path.startsWith("/contractiq/upload") ||
  path.startsWith("/prizelogic/");

/**
 * Cross-cutting gate that auto-resolves the current Word document (SharePoint library URL) into a generated contract.
 * Runs only when `Office.context.document.url` maps to a known doc-library path (see `documentRemotePathFromDocumentUrl`)
 * so blank/local files do not trigger list redirects or error banners after login.
 */
export const ResolveContractOnOpenGate = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [banner, setBanner] = useState<GateBanner | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const lastResolvedUrlRef = useRef<string | null>(null);
  const inflightRef = useRef(false);

  const hostDocState = useMemo(
    () => (location.state as HostDocReadyState | null) ?? null,
    [location.state]
  );

  useEffect(() => {
    if (!isAuthenticated()) return;
    if (inflightRef.current) return;
    if (hostDocState?.hostDocumentOpenedFromList) return;
    if (isReviewLikeRoute(location.pathname)) return;

    let cancelled = false;
    inflightRef.current = true;
    setBanner(null);

    void (async () => {
      try {
        const documentUrl = await getHostDocumentUrlAsync();
        if (cancelled) return;

        const normalizedUrl = documentUrl?.trim() ?? "";
        if (!normalizedUrl || !documentRemotePathFromDocumentUrl(normalizedUrl)) {
          return;
        }

        if (lastResolvedUrlRef.current === normalizedUrl) {
          return;
        }

        setIsResolving(true);
        lastResolvedUrlRef.current = normalizedUrl;

        const result: ResolveByDocumentUrlResult =
          await resolveGeneratedContractByDocumentUrl(normalizedUrl);
        if (cancelled) return;

        if (!result.ok) {
          if (result.errorCode === "UNAUTHORIZED") {
            // Allow automatic retry after the user signs back in.
            lastResolvedUrlRef.current = null;
          }
          setBanner({ tone: "error", message: result.errorMessage });
          return;
        }

        const decision = decideResolveNavigation({
          status: result.status,
          contractId: result.contractId
        });

        if (decision.action === "go_contract_list") {
          navigate("/contractiq/contracts", {
            replace: true,
            state: { resolveBanner: { tone: decision.tone, message: decision.message } }
          });
          return;
        }

        navigate(`/contractiq/contracts/${encodeURIComponent(decision.contractId)}/review`, {
          replace: true,
          state: {
            resolvedContract: result.contract ?? undefined,
            resolvedRaw: result.raw ?? undefined
          }
        });
      } finally {
        if (!cancelled) setIsResolving(false);
        inflightRef.current = false;
      }
    })();

    return () => {
      cancelled = true;
      inflightRef.current = false;
    };
  }, [hostDocState?.hostDocumentOpenedFromList, location.pathname, navigate]);

  if (!isAuthenticated()) return null;

  if (!isResolving && !banner) return null;

  const tone = banner?.tone ?? "info";
  const message = banner?.message ?? "Resolving this document…";
  const toneClass =
    tone === "error"
      ? "border-red-200 bg-red-50 text-red-800"
      : "border-amber-200 bg-amber-50 text-amber-900";

  return (
    <div
      className={`mb-4 rounded-md border p-3 text-sm ${toneClass}`}
      role={tone === "error" ? "alert" : "status"}
      aria-live="polite"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          {isResolving ? (
            <span
              className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"
              aria-hidden="true"
            />
          ) : null}
          <p className="leading-snug">{message}</p>
        </div>
        {banner ? (
          <button
            type="button"
            className="shrink-0 text-primary hover:underline"
            onClick={() => setBanner(null)}
          >
            Dismiss
          </button>
        ) : null}
      </div>
    </div>
  );
};

